import React, { useEffect, useRef } from 'react';
import '../App.css';
import '../components/Hero.css';

export default function ScylaPage() {
  const bgRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (bgRef.current) {
        const scrolled = window.scrollY;
        bgRef.current.style.transform = `scale(1.05) translateY(${scrolled * 0.3}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div>
      {/* ═══ HERO — same pattern as Home ═══ */}
      <section className="hero" id="top">
        <div className="hero-bg" ref={bgRef} style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/hero-bg.png)` }} />
        <div className="hero-overlay" />
        <div className="hero-content">
          <img src="/assets/NexusLogo.png" alt="Nexus Concordat" className="hero-logo" />
          <span className="hero-label">Nexus Concordat &middot; Scyla</span>
          <h1 className="hero-title">
            Scyla is your code base<br />
            to <span className="gold">govern your AI.</span>
          </h1>
          <p className="hero-sub">
            Imagine an AI that has native traceability to cited sources for every stdlib call, and compliance is compiled into the code, not bolted on afterwards.
          </p>
          <div className="hero-actions">
            <a href="https://scyla.nexusconcordat.com/code-bank/" className="btn-gold">Explore the Code Bank</a>
            <a href="mailto:caelsereith@aetherprotocols.com?subject=Scyla%20Partnership%20Inquiry" className="btn-outline">Request a Briefing</a>
          </div>
        </div>
        <div className="scroll-hint">
          <span>Scroll</span>
          <div className="scroll-line"><div className="scroll-fill" /></div>
        </div>
      </section>

      {/* ═══ GOLD DIVIDER ═══ */}
      <div style={{ padding: '3rem 0', display: 'flex', justifyContent: 'center' }}>
        <div style={{ height: 1, width: '80%', maxWidth: 900, background: 'linear-gradient(90deg, transparent, var(--gold), transparent)' }} />
      </div>

      {/* ═══ THE METAPHOR ═══ */}
      <section style={{ padding: '4rem 2rem 5rem' }}>
        <div className="section-inner" style={{ maxWidth: 820, textAlign: 'center' }}>
          <p style={{ fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)', fontFamily: "'Comfortaa', cursive", fontWeight: 400, lineHeight: 1.55, color: 'var(--text)', maxWidth: 700, margin: '0 auto 2rem', fontStyle: 'italic' }}>
            "Trucking companies got tired of waiting for their drivers to adhere to the rules of the road, so they governed the gas pedal."
          </p>
          <p style={{ fontSize: '1.05rem', color: 'var(--text-sub)', maxWidth: 600, margin: '0 auto', lineHeight: 1.8 }}>
            Scyla does the same thing for AI. Instead of training a model to follow rules and hoping it complies, <strong style={{ color: 'var(--gold)' }}>Scyla compiles the rules into the language itself.</strong> The AI cannot exceed the limit because the compiler will not produce code that does.
          </p>
        </div>
      </section>

      {/* ═══ GOLD DIVIDER ═══ */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ height: 1, width: '80%', maxWidth: 900, background: 'linear-gradient(90deg, transparent, var(--gold), transparent)' }} />
      </div>

      {/* ═══ HOW IT WORKS ═══ */}
      <section style={{ padding: '5rem 2rem', background: 'var(--bg-warm)' }}>
        <div className="section-inner">
          <span className="section-label">How it works</span>
          <h2 className="section-title" style={{ marginBottom: '2.5rem' }}>Compliance compiled in. Not bolted on.</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {[
              { n: '1', t: 'Native code calls, not retrieval', d: 'FDA drug labels, the periodic table, the human proteome, and the genetic code are compiled into the language as native function calls. There is no generation step. There is nothing to hallucinate.' },
              { n: '2', t: 'Every value cited to its source', d: 'Every atomic mass traces to IUPAC 2023. Every drug record traces to an FDA SPL label. Every protein sequence traces to UniProt. The citation is not metadata. It is the data.' },
              { n: '3', t: 'Deterministic and auditable', d: 'Same input, same output, every time. No probability distribution at the point of decision. An inspector can read the compiled rules, run them, and verify every output.' },
              { n: '4', t: 'Domain-specific Code Banks', d: 'The compiler is free. The domain-specific rule packs are licensed per deployment. Each deployment carries only the regulations its field answers to.' },
              { n: '5', t: 'Rule strictness types', d: 'Every compiled rule declares whether it is absolute, mandatory, discretionary, or advisory. The compiler enforces the difference.' },
              { n: '6', t: 'The human stays in the loop', d: 'Discretionary rules require a named human with documented reasoning before the action proceeds. Scyla makes human judgment auditable.' },
            ].map((f, i) => (
              <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '2rem', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, var(--gold), var(--gold-dim))', opacity: 0.6 }} />
                <div style={{ display: 'inline-block', width: '2.2rem', height: '2.2rem', background: 'var(--gold-glow)', color: 'var(--gold)', borderRadius: 6, fontWeight: 700, fontSize: '0.9rem', lineHeight: '2.2rem', textAlign: 'center', marginBottom: '1rem', fontFamily: "'Comfortaa', cursive" }}>{f.n}</div>
                <h3 style={{ fontFamily: "'Comfortaa', cursive", fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.6rem', color: 'var(--gold)' }}>{f.t}</h3>
                <p style={{ fontSize: '0.92rem', color: 'var(--text-sub)', lineHeight: 1.7, margin: 0 }}>{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ GOLD DIVIDER ═══ */}
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <div style={{ height: 1, width: '80%', maxWidth: 900, background: 'linear-gradient(90deg, transparent, var(--gold), transparent)' }} />
      </div>

      {/* ═══ WHAT SHIPS ═══ */}
      <section style={{ padding: '5rem 2rem' }}>
        <div className="section-inner">
          <span className="section-label">What ships with the compiler</span>
          <h2 className="section-title">The base stdlib is free. Forever.</h2>
          <p className="section-desc" style={{ marginBottom: '2.5rem' }}>Universal scientific reference data compiled into every Scyla binary. Cannot be shadowed, overridden, or removed.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1.2rem' }}>
            {[
              { n: '118', u: 'Elements', s: 'IUPAC 2023' },
              { n: '20,431', u: 'Human Proteins', s: 'UniProt' },
              { n: '3,002', u: 'FDA Drug Labels', s: 'DailyMed' },
              { n: '64', u: 'Codons', s: 'Genetic Code' },
              { n: '20', u: 'Amino Acids', s: 'Residue Masses' },
              { n: '44', u: 'Phonemes', s: 'IPA English' },
            ].map((s, i) => (
              <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '1.5rem', textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--gold)', fontFamily: "'Comfortaa', cursive", lineHeight: 1.2 }}>{s.n}</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text)', marginTop: '0.3rem' }}>{s.u}</div>
                <div style={{ fontSize: '0.68rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.2rem' }}>{s.s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PATENTS ═══ */}
      <section style={{ padding: '4rem 2rem', background: 'var(--bg-warm)', textAlign: 'center' }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '1rem' }}>
          {['63/939,190', '63/962,385', '63/988,485', '64/034,536'].map(p => (
            <span key={p} style={{ fontSize: '0.72rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--gold)', border: '1px solid var(--border-hover)', padding: '0.35rem 0.9rem', borderRadius: 100 }}>Patent Pending: {p}</span>
          ))}
        </div>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>Marjorie McCubbins &amp; Aislinn McCubbins — Nexus Concordat Inc.</p>
      </section>

      {/* ═══ CTA ═══ */}
      <section style={{ padding: '6rem 2rem', textAlign: 'center' }}>
        <div className="section-inner" style={{ maxWidth: 820 }}>
          <h2 className="section-title">Bring your field.</h2>
          <p style={{ fontSize: '1.05rem', color: 'var(--text-sub)', maxWidth: 540, margin: '0 auto 2rem', lineHeight: 1.7 }}>
            If your domain has published rules that need to be enforced, cited, and audited, Scyla compiles them into the language so your AI cannot violate them.
          </p>
          <a href="mailto:caelsereith@aetherprotocols.com?subject=Scyla%20-%20Partnership%20Inquiry" className="btn-gold">Request a Briefing</a>
          <p style={{ fontSize: '1.15rem', fontWeight: 600, color: 'var(--gold)', fontStyle: 'italic', marginTop: '3rem', fontFamily: "'Comfortaa', cursive" }}>Evolution is inevitable.</p>
        </div>
      </section>

      {/* ═══ SCYLA LOGO AT BOTTOM ═══ */}
      <section style={{ padding: '3rem 2rem', textAlign: 'center', borderTop: '1px solid var(--border)' }}>
        <img src="https://scyla.nexusconcordat.com/scyla-icon.png" alt="Scyla" style={{ maxHeight: 80, margin: '0 auto 1rem', opacity: 0.7, filter: 'brightness(1.3)' }} />
        <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)', fontFamily: "'Comfortaa', cursive", letterSpacing: '0.15em' }}>
          <a href="https://scyla.nexusconcordat.com" style={{ color: 'var(--gold)', textDecoration: 'none' }}>scyla.nexusconcordat.com</a>
        </p>
      </section>
    </div>
  );
}
